import type {
  DynamicModalAutocompleteOption,
  DynamicModalFieldConfig,
  User,
  UserGroup,
  UserRole,
} from '@e-learning/shared';
import {
  DynamicModal,
  DynamicModalFormValues,
  Toast,
  Users,
  iconCheckWhiteModalUsers,
  useTranslation,
} from '@e-learning/shared';
import { useCallback, useEffect, useState } from 'react';
import { useUsers } from './hooks';

/** Item de role retornado por fetchRoles (id, name, description) */
interface RoleListItem {
  id: string;
  name: string;
  description?: string;
}

function parseIds(s: string | undefined): string[] {
  return (s ?? '')
    .split(',')
    .map(x => x.trim())
    .filter(Boolean);
}

export default function UsersPage() {
  const { t } = useTranslation();
  const {
    fetchUsers,
    fetchUserGroups,
    fetchRoles,
    registerUser,
    updateUser,
    inactivateUser,
  } = useUsers();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [listKey, setListKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toastError, setToastError] = useState<string | null>(null);
  const [roleOptions, setRoleOptions] = useState<
    DynamicModalAutocompleteOption[]
  >([]);
  const [groupOptions, setGroupOptions] = useState<
    DynamicModalAutocompleteOption[]
  >([]);

  useEffect(() => {
    if (!toastError) return;
    const t = setTimeout(() => setToastError(null), 4000);
    return () => clearTimeout(t);
  }, [toastError]);

  useEffect(() => {
    if (!modalOpen) return;
    let cancelled = false;
    fetchRoles()
      .then((roles: RoleListItem[]) => {
        if (!cancelled) {
          setRoleOptions(
            roles.map((r: RoleListItem) => ({
              value: r.id,
              label: r.name,
              description: r.description,
            }))
          );
        }
      })
      .catch(() => {
        if (!cancelled) setRoleOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [modalOpen, fetchRoles]);

  const loadUserGroupOptions = useCallback(
    async ({
      search,
      page,
      pageSize,
    }: {
      search: string;
      page: number;
      pageSize: number;
    }) => {
      try {
        const { data, total } = await fetchUserGroups({
          page,
          limit: pageSize,
          search: search || undefined,
          includeInactive: false,
        });
        const options: DynamicModalAutocompleteOption[] = data.map(
          (g: UserGroup) => ({
            value: g.id,
            label: g.name ?? '',
          })
        );

        setGroupOptions(prev => {
          const map = new Map<string, DynamicModalAutocompleteOption>(
            prev.map(o => [o.value, o] as const)
          );
          options.forEach((o: DynamicModalAutocompleteOption) =>
            map.set(o.value, o)
          );
          return Array.from(map.values());
        });

        return {
          options,
          total,
          hasMore: page * pageSize < total,
        };
      } catch {
        if (page === 1) {
          setGroupOptions([]);
        }
        return {
          options: [],
          total: 0,
          hasMore: false,
        };
      }
    },
    [fetchUserGroups]
  );

  const refreshList = useCallback(() => {
    setListKey(k => k + 1);
  }, []);

  const handleAddUser = useCallback(() => {
    setEditingUser(null);
    setGroupOptions([]);
    setModalOpen(true);
  }, []);

  const handleCreateUser = useCallback(
    async (values: DynamicModalFormValues) => {
      if (isSubmitting) return;
      setIsSubmitting(true);
      try {
        await registerUser({
          name: (values.nameUser ?? '').trim(),
          email: (values.emailUser ?? '').trim(),
          roleIds: parseIds(values.rolesUser),
          userGroupIds: parseIds(values.groupUser),
        });
        setModalOpen(false);
        refreshList();
      } catch (err: unknown) {
        const data = (
          err as {
            response?: {
              data?: { errors?: Array<{ message?: string }>; message?: string };
            };
          }
        )?.response?.data;
        const messages = Array.isArray(data?.errors)
          ? (data.errors.map(e => e.message).filter(Boolean) as string[])
          : [];
        const msg = messages.length
          ? messages.join(' ')
          : (data?.message ?? (err instanceof Error ? err.message : ''));
        setToastError(msg || t('user_management.error_generic'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [registerUser, refreshList, isSubmitting, t]
  );

  const handleUpdateUser = useCallback(
    async (values: DynamicModalFormValues) => {
      if (!editingUser || isSubmitting) return;
      setIsSubmitting(true);
      try {
        await updateUser({
          id: editingUser.id,
          isActive: values.statusUser === 'true',
          name: (values.nameUser ?? '').trim(),
          email: (values.emailUser ?? '').trim(),
          roleIds: parseIds(values.rolesUser),
          userGroupIds: parseIds(values.groupUser),
        });
        setModalOpen(false);
        setEditingUser(null);
        refreshList();
      } catch (err: unknown) {
        const data = (
          err as {
            response?: {
              data?: { errors?: Array<{ message?: string }>; message?: string };
            };
          }
        )?.response?.data;
        const messages = Array.isArray(data?.errors)
          ? (data.errors.map(e => e.message).filter(Boolean) as string[])
          : [];
        const msg = messages.length
          ? messages.join(' ')
          : (data?.message ?? (err instanceof Error ? err.message : ''));
        setToastError(msg || t('user_management.error_generic'));
      } finally {
        setIsSubmitting(false);
      }
    },
    [editingUser, updateUser, refreshList, isSubmitting, t]
  );

  const handleEdit = useCallback((user: User) => {
    setEditingUser(user);

    // O `DynamicModal` renderiza os chips do autocomplete multiselect buscando
    // o `label` em `field.options`. Ao editar, `groupOptions` pode estar vazio
    // até o usuário buscar/filtrar — por isso o fallback aparece como `id`.
    // Pré-carregamos as opções selecionadas para exibir `name`.
    setGroupOptions(
      (user.userGroups ?? [])
        .filter(g => !!g?.id)
        .map(g => ({
          value: g.id,
          label: g.name ?? g.id,
        }))
    );

    setModalOpen(true);
  }, []);

  const handleDelete = useCallback((user: User) => {
    setUserToDelete(user);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete || isDeleting) return;
    setIsDeleting(true);
    try {
      await inactivateUser(userToDelete.id);
      setUserToDelete(null);
      refreshList();
    } catch {
      setToastError(t('user_management.error_generic'));
    } finally {
      setIsDeleting(false);
    }
  }, [userToDelete, isDeleting, inactivateUser, refreshList, t]);

  const isEditMode = editingUser != null;
  const modalInitialValues: Partial<DynamicModalFormValues> = isEditMode
    ? {
        nameUser: editingUser?.name ?? '',
        emailUser: editingUser?.email ?? '',
        rolesUser: (editingUser?.roles ?? [])
          .map((r: UserRole) =>
            typeof r === 'object' && r && 'id' in r ? r.id : ''
          )
          .filter(Boolean)
          .join(','),
        groupUser: (editingUser?.userGroups ?? [])
          .map((g: UserGroup) => g?.id ?? '')
          .filter(Boolean)
          .join(','),
        statusUser: editingUser?.isActive !== false ? 'true' : 'false',
      }
    : {};

  const modalFields: DynamicModalFieldConfig[] = [
    {
      name: 'nameUser',
      label: t('user_management.modal_field_name_label'),
      type: 'text' as const,
      placeholder: t('user_management.modal_field_name_placeholder'),
      optional: false,
    },
    {
      name: 'emailUser',
      label: t('user_management.modal_field_email_label'),
      type: 'email' as const,
      placeholder: t('user_management.modal_field_email_placeholder'),
      optional: false,
    },
    {
      name: 'rolesUser',
      label: t('user_management.modal_field_roles_label'),
      type: 'checkboxgroup' as DynamicModalFieldConfig['type'],
      placeholder: t('user_management.modal_field_roles_placeholder'),
      optional: true,
      options: roleOptions,
    },
    {
      name: 'groupUser',
      label: t('user_management.modal_field_group_label'),
      type: 'autocomplete' as const,
      placeholder: t('user_management.modal_field_group_placeholder'),
      optional: true,
      options: groupOptions,
      multiselect: true,
      loadOptions: loadUserGroupOptions,
      pageSize: 3,
    },
    ...(isEditMode
      ? [
          {
            name: 'statusUser',
            label: t('user_management.modal_field_status_label'),
            type: 'switch' as const,
            placeholder: t('user_management.modal_field_status_placeholder'),
            optional: false,
          },
        ]
      : []),
  ];

  const modalActions = isEditMode
    ? [
        {
          label: t('user_management.modal_action_cancel'),
          onClick: () => {
            setModalOpen(false);
            setEditingUser(null);
          },
          variant: 'secondary' as const,
          outlined: true,
        },
        {
          label: t('user_management.modal_action_save'),
          onClick: handleUpdateUser,
          variant: 'primary' as const,
          iconSrc: iconCheckWhiteModalUsers,
          iconSize: 18,
          iconAlt: t('user_management.modal_action_save'),
        },
      ]
    : [
        {
          label: t('user_management.modal_action_cancel'),
          onClick: () => setModalOpen(false),
          variant: 'secondary' as const,
          outlined: true,
        },
        {
          label: t('user_management.modal_action_create'),
          onClick: handleCreateUser,
          variant: 'primary' as const,
          iconSrc: iconCheckWhiteModalUsers,
          iconSize: 18,
          iconAlt: t('user_management.modal_action_create'),
        },
      ];

  return (
    <div data-e2e="page-admin-users">
      {toastError && (
        <Toast message={toastError} variant="error" role="alert" />
      )}
      <Users
        key={listKey}
        fetchUsers={fetchUsers}
        onAddUser={handleAddUser}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      <DynamicModal
        title={
          isEditMode
            ? t('user_management.modal_edit_title')
            : t('user_management.modal_create_title')
        }
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditingUser(null);
        }}
        closeOnOverlayClick={false}
        initialValues={modalInitialValues}
        fields={modalFields}
        actions={modalActions}
      />
      <DynamicModal
        title={
          userToDelete
            ? t('user_management.delete_confirm_title', {
                name: userToDelete.name || userToDelete.email,
              })
            : ''
        }
        description={t('user_management.delete_confirm_message')}
        compact
        open={!!userToDelete}
        onClose={() => !isDeleting && setUserToDelete(null)}
        closeOnOverlayClick={false}
        fields={[]}
        actions={[
          {
            label: t('user_management.modal_action_cancel'),
            onClick: () => setUserToDelete(null),
            variant: 'secondary',
          },
          {
            label: isDeleting
              ? t('user_management.modal_action_deleting')
              : t('user_management.modal_action_delete'),
            onClick: handleConfirmDelete,
            variant: 'primary',
          },
        ]}
        dataE2ePrefix="users-delete-confirm"
      />
    </div>
  );
}
